import { recordAction } from "@/lib/actionLedger";
import { auth, db } from "@/lib/firebase";
import { ScheduledJobService } from "@/lib/scheduledJobService";
import {
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

// Domain Integration Types
export interface GoogleAnalyticsIntegration {
  selectedAccountId: string | null;
  selectedPropertyId: string | null;
  is_enabled: boolean;
  connectedBy: string | null;
  connectedAt: Timestamp | null;
  connectedAccountEmail: string | null;
  connectedPropertyName: string | null;
}

export interface GoogleSearchConsoleIntegration {
  selectedAccountId: string | null;
  selectedSiteUrl: string | null;
  is_enabled: boolean;
  connectedBy: string | null;
  connectedAt: Timestamp | null;
  connectedAccountEmail: string | null;
}

export interface DomainIntegration {
  domainId: string;
  googleAnalytics: GoogleAnalyticsIntegration;
  googleSearchConsole: GoogleSearchConsoleIntegration;
  // Future integrations can be added here:
  // facebook?: FacebookIntegration;
  // linkedIn?: LinkedInIntegration;
}

export interface DomainIntegrationsSlice {
  /* state */
  domainIntegrations: Record<string, DomainIntegration>; // keyed by domainId
  currentDomainIntegrations: DomainIntegration | null;
  isLoadingDomainIntegrations: boolean;
  domainIntegrationsError: string | null;

  /* actions */
  subscribeToDomainIntegrations: (
    orgId: string,
    domainId: string,
  ) => () => void;
  setCurrentDomainIntegrations: (domainId: string | null) => void;
  updateGoogleAnalyticsIntegration: (
    orgId: string,
    domainId: string,
    integration: Partial<GoogleAnalyticsIntegration>,
  ) => Promise<void>;
  updateGoogleSearchConsoleIntegration: (
    orgId: string,
    domainId: string,
    integration: Partial<GoogleSearchConsoleIntegration>,
  ) => Promise<void>;
  deleteGoogleSearchConsoleIntegration: (
    orgId: string,
    domainId: string,
  ) => Promise<void>;
  unsubscribeDomainIntegrations: () => void;

  /* computed getters */
  getCurrentGoogleAnalyticsIntegration: () => GoogleAnalyticsIntegration;
  getCurrentGoogleSearchConsoleIntegration: () => GoogleSearchConsoleIntegration;
  getIsConnectionOwner: (
    integration: { connectedBy: string | null },
  ) => boolean;

  /* internals */
  _domainIntegrationsUnsubscribe: (() => void) | null;
}

const defaultGoogleAnalyticsIntegration: GoogleAnalyticsIntegration = {
  selectedAccountId: null,
  selectedPropertyId: null,
  is_enabled: false,
  connectedBy: null,
  connectedAt: null,
  connectedAccountEmail: null,
  connectedPropertyName: null,
};

const defaultGoogleSearchConsoleIntegration: GoogleSearchConsoleIntegration = {
  selectedAccountId: null,
  selectedSiteUrl: null,
  is_enabled: false,
  connectedBy: null,
  connectedAt: null,
  connectedAccountEmail: null,
};

export const createDomainIntegrationsSlice: StateCreator<
  StoreState,
  [],
  [],
  DomainIntegrationsSlice
> = (set, get) => ({
  /* ---------- initial state ---------- */
  domainIntegrations: {},
  currentDomainIntegrations: null,
  isLoadingDomainIntegrations: false,
  domainIntegrationsError: null,
  _domainIntegrationsUnsubscribe: null,

  /* ---------- actions ---------- */
  subscribeToDomainIntegrations: (orgId: string, domainId: string) => {
    if (!orgId || !domainId) {
      console.log(
        "Domain integrations subscription requires orgId and domainId.",
      );
      return () => {};
    }

    // Clean up any existing subscription
    get()._domainIntegrationsUnsubscribe?.();
    set({
      isLoadingDomainIntegrations: true,
      domainIntegrationsError: null,
    });

    console.log(`Setting up domain integrations listener for ${domainId}`);

    // Subscribe to integration settings only
    const googleAnalyticsRef = doc(
      db,
      `organizations/${orgId}/domains/${domainId}/integrations`,
      "google-analytics",
    );

    const unsubscribeGoogleAnalytics = onSnapshot(
      googleAnalyticsRef,
      (doc) => {
        const integrationData = doc.exists() ? doc.data() : {};

        const googleAnalytics: GoogleAnalyticsIntegration = {
          selectedAccountId: integrationData.selectedAccountId || null,
          selectedPropertyId: integrationData.selectedPropertyId || null,
          is_enabled: integrationData.is_enabled ?? false,
          connectedBy: integrationData.connectedBy || null,
          connectedAt: integrationData.connectedAt || null,
          connectedAccountEmail: integrationData.connectedAccountEmail || null,
          connectedPropertyName: integrationData.connectedPropertyName || null,
        };

        console.log(`Updating domain integrations for ${domainId}:`, {
          integrationSettings: integrationData,
        });

        set((state) => {
          const existing = state.domainIntegrations[domainId] || {
            domainId,
            googleAnalytics: defaultGoogleAnalyticsIntegration,
            googleSearchConsole: defaultGoogleSearchConsoleIntegration,
          };
          const domainIntegration: DomainIntegration = {
            ...existing,
            domainId,
            googleAnalytics,
          };

          return {
            domainIntegrations: {
              ...state.domainIntegrations,
              [domainId]: domainIntegration,
            },
            currentDomainIntegrations: domainIntegration,
            isLoadingDomainIntegrations: false,
          };
        });
      },
      (error) => {
        console.error(
          `Domain integrations subscription error for ${domainId}:`,
          error,
        );
        set({
          domainIntegrationsError: `Failed to subscribe to domain integrations: ${error.message}`,
          isLoadingDomainIntegrations: false,
        });
      },
    );

    const googleSearchConsoleRef = doc(
      db,
      `organizations/${orgId}/domains/${domainId}/integrations`,
      "google-search-console",
    );

    const unsubscribeGoogleSearchConsole = onSnapshot(
      googleSearchConsoleRef,
      (doc) => {
        const integrationData = doc.exists() ? doc.data() : {};

        const googleSearchConsole: GoogleSearchConsoleIntegration = {
          selectedAccountId: integrationData.selectedAccountId || null,
          selectedSiteUrl: integrationData.selectedSiteUrl || null,
          is_enabled: integrationData.is_enabled ?? false,
          connectedBy: integrationData.connectedBy || null,
          connectedAt: integrationData.connectedAt || null,
          connectedAccountEmail: integrationData.connectedAccountEmail || null,
        };

        set((state) => {
          const existing = state.domainIntegrations[domainId] || {
            domainId,
            googleAnalytics: defaultGoogleAnalyticsIntegration,
            googleSearchConsole: defaultGoogleSearchConsoleIntegration,
          };
          const domainIntegration: DomainIntegration = {
            ...existing,
            domainId,
            googleSearchConsole,
          };

          return {
            domainIntegrations: {
              ...state.domainIntegrations,
              [domainId]: domainIntegration,
            },
            currentDomainIntegrations: domainIntegration,
            isLoadingDomainIntegrations: false,
          };
        });
      },
      (error) => {
        console.error(
          `Google Search Console integration subscription error for ${domainId}:`,
          error,
        );
        set({
          domainIntegrationsError: `Failed to subscribe to domain integrations: ${error.message}`,
          isLoadingDomainIntegrations: false,
        });
      },
    );

    const unsubscribe = () => {
      unsubscribeGoogleAnalytics();
      unsubscribeGoogleSearchConsole();
    };

    set({ _domainIntegrationsUnsubscribe: unsubscribe });
    return unsubscribe;
  },

  setCurrentDomainIntegrations: (domainId: string | null) => {
    if (!domainId) {
      set({ currentDomainIntegrations: null });
      return;
    }

    const existingIntegration = get().domainIntegrations[domainId];
    if (existingIntegration) {
      set({ currentDomainIntegrations: existingIntegration });
    } else {
      // Create default integration for new domain
      const defaultIntegration: DomainIntegration = {
        domainId,
        googleAnalytics: defaultGoogleAnalyticsIntegration,
        googleSearchConsole: defaultGoogleSearchConsoleIntegration,
      };
      set({
        currentDomainIntegrations: defaultIntegration,
        domainIntegrations: {
          ...get().domainIntegrations,
          [domainId]: defaultIntegration,
        },
      });
    }
  },

  updateGoogleAnalyticsIntegration: async (
    orgId: string,
    domainId: string,
    integration: Partial<GoogleAnalyticsIntegration>,
  ) => {
    if (!orgId || !domainId) {
      throw new Error("Organization ID and Domain ID are required");
    }

    try {
      const existingConnectedBy =
        get().domainIntegrations[domainId]?.googleAnalytics?.connectedBy;

      // Update local state immediately, merging partial into existing
      set((state) => {
        const existing = state.domainIntegrations[domainId];
        const mergedGoogleAnalytics: GoogleAnalyticsIntegration = {
          ...(existing?.googleAnalytics || defaultGoogleAnalyticsIntegration),
          ...integration,
        } as GoogleAnalyticsIntegration;

        const updatedIntegration: DomainIntegration = {
          ...existing,
          domainId,
          googleAnalytics: mergedGoogleAnalytics,
          googleSearchConsole:
            existing?.googleSearchConsole ||
            defaultGoogleSearchConsoleIntegration,
        };

        return {
          domainIntegrations: {
            ...state.domainIntegrations,
            [domainId]: updatedIntegration,
          },
          currentDomainIntegrations:
            state.currentDomainIntegrations?.domainId === domainId
              ? updatedIntegration
              : state.currentDomainIntegrations,
        };
      });

      // Persist to Firebase. `connectedBy` is pinned to the caller's uid by
      // firestore.rules and is immutable after create -- so a connect write must
      // carry it, and an update by a non-owner will be rejected by the rules.
      const uid = auth.currentUser?.uid;
      if (!uid) {
        throw new Error("Cannot update integration: no authenticated user");
      }

      const integrationsRef = doc(
        db,
        `organizations/${orgId}/domains/${domainId}/integrations`,
        "google-analytics",
      );

      // Stamp ownership only on the first connect -- i.e. the doc has no owner
      // yet. Re-including selectedAccountId on a later edit (e.g. changing the
      // property) must NOT re-stamp connectedBy/connectedAt, and firestore.rules
      // forbid a non-owner from changing connectedBy on an update in any case.
      const isFirstConnect =
        integration.selectedAccountId != null && !existingConnectedBy;
      await setDoc(
        integrationsRef,
        isFirstConnect
          ? { ...integration, connectedBy: uid, connectedAt: serverTimestamp() }
          : integration,
        { merge: true },
      );
      // Only the first connect is an event worth recording; later edits just
      // change which property is selected on an already-connected integration.
      if (isFirstConnect) {
        await recordAction({
          orgId,
          actorUid: uid,
          action: "integration.connected",
          targetPath: integrationsRef.path,
          domainId,
          summary: "Google Analytics",
        });
        // Arm the weekly analytics-snapshot job. Best-effort: the integration
        // write above already succeeded and is the user's actual intent, so a
        // failure here must not fail the connect or surface as an error.
        try {
          await ScheduledJobService.ensureAnalyticsSnapshotJob(orgId, domainId);
        } catch (error) {
          console.error(
            `Failed to arm the analytics snapshot job for domain ${domainId}:`,
            error,
          );
        }
      }
      console.log(
        `Updated Google Analytics integration for domain ${domainId}:`,
        integration,
      );
    } catch (error) {
      console.error("Failed to update Google Analytics integration:", error);
      set({
        domainIntegrationsError: `Failed to update integration: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
      throw error;
    }
  },

  updateGoogleSearchConsoleIntegration: async (
    orgId: string,
    domainId: string,
    integration: Partial<GoogleSearchConsoleIntegration>,
  ) => {
    if (!orgId || !domainId) {
      throw new Error("Organization ID and Domain ID are required");
    }

    try {
      const existingConnectedBy =
        get().domainIntegrations[domainId]?.googleSearchConsole?.connectedBy;

      set((state) => {
        const existing = state.domainIntegrations[domainId];
        const mergedGoogleSearchConsole: GoogleSearchConsoleIntegration = {
          ...(existing?.googleSearchConsole ||
            defaultGoogleSearchConsoleIntegration),
          ...integration,
        };

        const updatedIntegration: DomainIntegration = {
          ...existing,
          domainId,
          googleAnalytics:
            existing?.googleAnalytics || defaultGoogleAnalyticsIntegration,
          googleSearchConsole: mergedGoogleSearchConsole,
        };

        return {
          domainIntegrations: {
            ...state.domainIntegrations,
            [domainId]: updatedIntegration,
          },
          currentDomainIntegrations:
            state.currentDomainIntegrations?.domainId === domainId
              ? updatedIntegration
              : state.currentDomainIntegrations,
        };
      });

      // Persist to Firebase. `connectedBy` is pinned to the caller's uid by
      // firestore.rules and is immutable after create -- so a connect write must
      // carry it, and an update by a non-owner will be rejected by the rules.
      const uid = auth.currentUser?.uid;
      if (!uid) {
        throw new Error("Cannot update integration: no authenticated user");
      }

      const integrationsRef = doc(
        db,
        `organizations/${orgId}/domains/${domainId}/integrations`,
        "google-search-console",
      );

      // Stamp ownership only on the first connect -- i.e. the doc has no owner
      // yet. Re-including selectedAccountId on a later edit (e.g. changing the
      // site) must NOT re-stamp connectedBy/connectedAt, and firestore.rules
      // forbid a non-owner from changing connectedBy on an update in any case.
      const isFirstConnect =
        integration.selectedAccountId != null && !existingConnectedBy;
      await setDoc(
        integrationsRef,
        isFirstConnect
          ? { ...integration, connectedBy: uid, connectedAt: serverTimestamp() }
          : integration,
        { merge: true },
      );
      // Only the first connect is an event worth recording; later edits just
      // change which site is selected on an already-connected integration.
      if (isFirstConnect) {
        await recordAction({
          orgId,
          actorUid: uid,
          action: "integration.connected",
          targetPath: integrationsRef.path,
          domainId,
          summary: "Google Search Console",
        });
        // Arm the weekly analytics-snapshot job -- but only once GSC is
        // actually a snapshot source. Unlike GA4 (existence-only), core's
        // snapshot repository gates GSC on `is_enabled`, which
        // useDomainIntegrations only sets once an account AND a site are
        // chosen. Arming on `selectedAccountId` alone (e.g. an account picked
        // before a site) would arm a job with no enabled source to run
        // against -- best-effort like the write above: it must not fail the
        // connect or surface as an error.
        if (integration.selectedAccountId != null && integration.selectedSiteUrl != null) {
          try {
            await ScheduledJobService.ensureAnalyticsSnapshotJob(orgId, domainId);
          } catch (error) {
            console.error(
              `Failed to arm the analytics snapshot job for domain ${domainId}:`,
              error,
            );
          }
        }
      }
      console.log(
        `Updated Google Search Console integration for domain ${domainId}:`,
        integration,
      );
    } catch (error) {
      console.error(
        "Failed to update Google Search Console integration:",
        error,
      );
      set({
        domainIntegrationsError: `Failed to update integration: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
      throw error;
    }
  },

  deleteGoogleSearchConsoleIntegration: async (
    orgId: string,
    domainId: string,
  ) => {
    if (!orgId || !domainId) {
      throw new Error("Organization ID and Domain ID are required");
    }

    try {
      // Optimistically reset local state to the default (disconnected)
      // shape. Disconnect must DELETE the doc rather than null-merge it:
      // core's GoogleSearchConsoleDomainIntegration model requires
      // selectedAccountId/selectedSiteUrl as non-null strings, so a
      // null-merge leaves a doc that fails model_validate on every read
      // while still carrying is_enabled: true and connectedBy -- unrecoverable
      // by anyone but the original owner. Firestore rules allow `delete` for
      // any org admin, which is the sanctioned recovery path.
      set((state) => {
        const existing = state.domainIntegrations[domainId];
        const updatedIntegration: DomainIntegration = {
          ...existing,
          domainId,
          googleAnalytics:
            existing?.googleAnalytics || defaultGoogleAnalyticsIntegration,
          googleSearchConsole: defaultGoogleSearchConsoleIntegration,
        };

        return {
          domainIntegrations: {
            ...state.domainIntegrations,
            [domainId]: updatedIntegration,
          },
          currentDomainIntegrations:
            state.currentDomainIntegrations?.domainId === domainId
              ? updatedIntegration
              : state.currentDomainIntegrations,
        };
      });

      const integrationsRef = doc(
        db,
        `organizations/${orgId}/domains/${domainId}/integrations`,
        "google-search-console",
      );
      await recordAction({
        orgId,
        actorUid: get().currentUser?.uid,
        action: "integration.disconnected",
        targetPath: integrationsRef.path,
        domainId,
        summary: "Google Search Console",
      });
      await deleteDoc(integrationsRef);
      console.log(
        `Deleted Google Search Console integration for domain ${domainId}`,
      );
    } catch (error) {
      console.error(
        "Failed to delete Google Search Console integration:",
        error,
      );
      set({
        domainIntegrationsError: `Failed to delete integration: ${error instanceof Error ? error.message : "Unknown error"}`,
      });
      throw error;
    }
  },

  unsubscribeDomainIntegrations: () => {
    const unsubscribe = get()._domainIntegrationsUnsubscribe;
    if (unsubscribe) {
      console.log("Unsubscribing from domain integrations listener.");
      unsubscribe();
      set({ _domainIntegrationsUnsubscribe: null });
    }
  },

  /* ---------- computed getters ---------- */
  getCurrentGoogleAnalyticsIntegration: () => {
    const currentIntegrations = get().currentDomainIntegrations;
    return (
      currentIntegrations?.googleAnalytics || defaultGoogleAnalyticsIntegration
    );
  },
  getCurrentGoogleSearchConsoleIntegration: () => {
    const currentIntegrations = get().currentDomainIntegrations;
    return (
      currentIntegrations?.googleSearchConsole ||
      defaultGoogleSearchConsoleIntegration
    );
  },
  getIsConnectionOwner: (integration) => {
    const uid = auth.currentUser?.uid;
    return !!uid && integration.connectedBy === uid;
  },
});
